/* 지금 보고 있는 방향(나침반).
 *
 * 위치와는 다른 API 다. 좌표는 geolocation 이 주지만 방위는 DeviceOrientation 이 준다 —
 * 권한도 따로고, 없는 기기도 따로다. 그래서 훅도 따로 둔다.
 *
 * **데스크톱에는 나침반이 없다.** 맥·PC 브라우저는 이 이벤트를 아예 안 쏘거나 방위가
 * 아닌 상대 각도만 준다. 그때는 null 을 돌려주고, 화면은 부채꼴 없이 점만 그린다 —
 * 모르는 방향을 그럴듯하게 지어내는 것보다 안 그리는 게 낫다.
 *
 * geolocation 의 coords.heading 은 안 쓴다. 그건 나침반이 아니라 '이동 방향'이라
 * 서 있으면 null 이고, Wi-Fi 로 위치를 잡는 기기에서는 늘 null 이다.
 */

import { useEffect, useRef, useState } from "react";

/** 화면을 돌려 쥐면 기기의 위쪽과 화면의 위쪽이 어긋난다. 세로로 쓰면 0 이라 티가 안 난다. */
const screenAngle = () => screen.orientation?.angle ?? 0;

/**
 * 이벤트 하나에서 진북 기준 방위를 꺼낸다. 못 꺼내면 null.
 *
 * iOS 는 absolute 이벤트가 없는 대신 webkitCompassHeading(이미 진북 기준, 시계방향)을
 * 얹어준다. 안드로이드는 deviceorientationabsolute 의 alpha 인데 **반시계방향**이라
 * 360 에서 빼야 나침반과 같은 방향이 된다.
 *
 * absolute 가 아닌 이벤트는 버린다. 그건 '켤 때 향하던 쪽 기준 상대 각도'라 방위가
 * 아니다 — 그걸 그리면 부채꼴이 엉뚱한 데를 가리키면서도 자신 있어 보인다.
 */
function headingOf(e) {
  const raw = typeof e.webkitCompassHeading === "number" ? e.webkitCompassHeading
    : e.absolute === true && typeof e.alpha === "number" ? (360 - e.alpha) % 360
    : null;
  return raw == null ? null : (raw + screenAngle()) % 360;
}

/**
 * 진북 기준 각도(도). 나침반이 없으면 null.
 *
 * 돌려주는 값은 **계속 이어지는 각**이라 360 을 넘거나 음수가 될 수 있다. 0~359 로
 * 접어서 주면 359° 다음에 1° 가 올 때 CSS 가 358도를 거꾸로 돌아간다 — 화면에서는
 * 한 걸음 돌았을 뿐인데 부채꼴이 팽이처럼 한 바퀴 되감긴다. 최단 차이를 누적해 두면
 * 회전이 늘 짧은 쪽으로 간다.
 */
export function useHeading() {
  const [deg, setDeg] = useState(null);
  const total = useRef(0);   // 누적된 연속 각
  const last = useRef(null); // 직전에 받은 0~359 방위

  useEffect(() => {
    const on = (e) => {
      const h = headingOf(e);
      if (h == null) return;
      /* 나침반은 가만히 있어도 1도씩 떨린다. 그대로 흘리면 초당 60번 다시 그린다.
         2도로 뭉개면 눈에 보이는 움직임은 그대로면서 갱신이 확 준다. */
      const q = Math.round(h / 2) * 2 % 360;
      if (q === last.current) return;
      total.current = last.current == null
        ? q
        : total.current + (((q - last.current + 540) % 360) - 180);
      last.current = q;
      setDeg(total.current);
    };
    // 안드로이드는 absolute 쪽에, iOS 는 평범한 쪽에 온다. 둘 다 걸어둔다.
    window.addEventListener("deviceorientationabsolute", on);
    window.addEventListener("deviceorientation", on);
    return () => {
      window.removeEventListener("deviceorientationabsolute", on);
      window.removeEventListener("deviceorientation", on);
    };
  }, []);

  return deg;
}

/**
 * iOS 는 나침반에도 따로 허락을 받는다. **클릭과 같은 태스크에서 불러야 한다** —
 * await 뒤에서 부르면 사용자 제스처가 아니라며 조용히 거절된다(팝업과 같은 규칙).
 *
 * 거절돼도 알리지 않는다. 방향은 덤이라, 못 받으면 부채꼴 없이 점만 그리면 된다 —
 * 위치는 멀쩡히 나오는데 토스트가 뜨면 그게 더 고장처럼 보인다.
 */
export function askHeading() {
  const D = window.DeviceOrientationEvent;
  if (typeof D?.requestPermission === "function") D.requestPermission().catch(() => {});
}
